/**
 * providerPolicyErrors (client mirror) — upstream PROVIDER-POLICY failure
 * classification for the browser runtime (2026-08-11, INCIDENT-20260811).
 *
 * The server-side source of truth is functions/src/lib/providerPolicyErrors.ts.
 * The frontend bundle cannot import from functions/, so the regexes are
 * DUPLICATED here and functions/src/__tests__/providerPolicyErrors.test.ts
 * imports both modules and fails if they drift — the same lockstep discipline
 * lib/upstreamLogSeverity uses against prest0nVm/creditGuard.
 *
 * What the client does with a policy verdict (see openRouterClient.ts and
 * services/translator.ts): treat it as a RUNG failure, not a terminal error.
 * The next model on the ladder usually serves, because a policy block is
 * scoped to a model/provider — unlike the payable class, where every rung
 * bills the same exhausted account and descending only burns latency.
 *
 * Defence in depth: with the proxies now descending server-side and returning
 * a sanitized payload (safeUpstreamErrorPayload), a raw provider body should
 * never reach this layer at all. This module stays because the frontend also
 * talks to non-proxy surfaces, and because a leak that reaches the customer's
 * screen is exactly the failure INCIDENT-20260811 was about.
 */

export type ProviderPolicyKind = 'age-gate' | 'moderation' | 'data-policy' | 'restricted';

/** MIRROR of functions/src/lib/providerPolicyErrors.ts — keep byte-identical. */
export const POLICY_AGE_GATE_WORDING =
  /age[- ]?(?:verification|confirmation|gate|check)|(?:verify|confirm)(?:ing)? (?:your )?age|18\s?\+|must be (?:at least )?18|adult content/i;

/** MIRROR of functions/src/lib/providerPolicyErrors.ts — keep byte-identical. */
export const POLICY_DATA_POLICY_WORDING =
  /data policy|privacy (?:policy|setting)|no endpoints found matching|zero[- ]data[- ]retention|\bzdr\b/i;

/** MIRROR of functions/src/lib/providerPolicyErrors.ts — keep byte-identical. */
export const POLICY_MODERATION_WORDING =
  /moderation|flagged (?:by|as|for)|content (?:policy|filter|violation)|prohibited content|safety (?:system|filter)|usage polic(?:y|ies)|acceptable use/i;

/**
 * MIRROR of prest0nVm/creditGuard's PROVIDER_CREDIT_WORDING. Present so the
 * client honours the same precedence rule as the server: payable/auth beats
 * policy, because topping up and descending are different actions.
 */
export const PROVIDER_CREDIT_WORDING =
  /credit balance is too low|insufficient[_ ]credits?|requires more credits|key limit exceeded|spend(?:ing)? limit|billing hard limit|exceeded your current quota|payment required/i;

/** 403 forbidden-by-policy, 451 legal reasons. 401 is the auth class, not ours. */
const POLICY_STATUSES: ReadonlySet<number> = new Set([403, 451]);

/** Code the proxies stamp on a sanitized policy rejection. */
export const PROVIDER_POLICY_ERROR_CODE = 'UPSTREAM_POLICY_BLOCKED';

/** MIRROR of the server copy — the only message a customer sees on total block. */
// copy: provider_policy_failure
export const PROVIDER_POLICY_USER_MESSAGE =
  "I couldn't complete that request on any available model. My team has been alerted — there's nothing you need to do.";

/**
 * Classify an upstream failure as provider-policy. Pure; null means not ours.
 * Mirrors the server decision table, including its precedence order.
 */
export function classifyProviderPolicyFailure(
  status: number,
  errorText: string,
): ProviderPolicyKind | null {
  const text = String(errorText || '');

  // Payable/auth wins (mirrors the server's classifyUpstreamFailureForLog gate).
  if (status === 401 || status === 402 || PROVIDER_CREDIT_WORDING.test(text)) return null;

  if (POLICY_AGE_GATE_WORDING.test(text)) return 'age-gate';
  if (POLICY_DATA_POLICY_WORDING.test(text)) return 'data-policy';
  if (POLICY_MODERATION_WORDING.test(text)) return 'moderation';
  if (POLICY_STATUSES.has(status)) return 'restricted';
  return null;
}

/**
 * Payable/auth failures (credits, spend limit, dead key). Split out because the
 * ACTION differs: descending the ladder cannot help when every rung bills the
 * same exhausted account, so these stay terminal on the client and page ops
 * server-side (INCIDENT-20260722).
 */
export function isPayableUpstreamFailure(err: unknown): boolean {
  const e = err as { status?: number; message?: string } | null;
  if (!e) return false;
  if (e.status === 401 || e.status === 402) return true;
  return PROVIDER_CREDIT_WORDING.test(String(e.message || ''));
}

// ── Customer-facing scrubbing (MIRROR of functions/src/lib/providerPolicyErrors) ──
// Keep these three constants and scrubUpstreamErrorText byte-identical to the
// server copy; the lockstep test compares them.

const URL_PATTERN = /\bhttps?:\/\/\S+/gi;
const BARE_DOMAIN_PATTERN =
  /\b(?:[a-z0-9-]+\.)+(?:ai|com|io|dev|org|net|co)\b(?:\/\S*)?/gi;
const ACCOUNT_INSTRUCTION_WORDING =
  /\b(?:settings|preferences|dashboard|console|billing|api ?key|sign in|log ?in|your account|account settings)\b/i;

/**
 * Drop URLs, bare domains and account-instruction sentences from an upstream
 * error string. Returns '' when nothing survives — callers substitute fixed
 * copy rather than shipping a fragment.
 */
export function scrubUpstreamErrorText(raw: string): string {
  const kept = String(raw || '')
    // Split on sentence boundaries AND newlines — provider bodies are often a
    // single line with the instruction appended after a period.
    .split(/(?<=[.!?])\s+|\n+/)
    // Drop the WHOLE sentence when it carries a link or an account errand.
    // Stripping the URL in place and keeping the rest leaves dangling
    // fragments ("Please confirm your age at") that read as an instruction the
    // customer still cannot act on — the 2026-08-11 harm minus the hyperlink.
    .filter((sentence) => {
      if (!sentence.trim()) return false;
      if (ACCOUNT_INSTRUCTION_WORDING.test(sentence)) return false;
      URL_PATTERN.lastIndex = 0;
      BARE_DOMAIN_PATTERN.lastIndex = 0;
      return !URL_PATTERN.test(sentence) && !BARE_DOMAIN_PATTERN.test(sentence);
    })
    .join(' ');
  // Belt and braces: a link that survived sentence splitting never ships.
  return kept
    .replace(URL_PATTERN, ' ')
    .replace(BARE_DOMAIN_PATTERN, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * Error-object form of the above, for the client's rung-advance predicates.
 * Reads the shape both `openRouterClient` and `translator` throw:
 * `Error & { status?: number; code?: string }`.
 */
export function isProviderPolicyBlocked(err: unknown): boolean {
  const e = err as { status?: number; code?: string; message?: string } | null;
  if (!e) return false;
  if (e.code === PROVIDER_POLICY_ERROR_CODE) return true;
  const status = typeof e.status === 'number' ? e.status : 0;
  const text = [e.message, e.code].filter(Boolean).join(' ');
  return classifyProviderPolicyFailure(status, text) !== null;
}
